import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Wallet, CreditCard, ArrowLeftRight,
  FileText, Settings, LogOut, ChevronRight, Shield, Database, Search, Building2, ClipboardCheck, Coins, Globe, X, Wrench, LifeBuoy, UserCog
} from 'lucide-react';
import { useAdminAuthStore } from '../../store/adminAuthStore';
import { useSelectedClientStore } from '../../store/selectedClientStore';
import { formatPublicIdDisplay } from '../../utils/formatPublicId';
import { getClientDisplayName } from '../../utils/clientDisplay';

const CLIENT_SCOPED_ROUTES = new Set(['/accounts', '/payments', '/fx', '/reports']);

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/clients', icon: Users, label: 'Clients' },
  { to: '/onboarding', icon: ClipboardCheck, label: 'Onboarding' },
  { to: '/accounts', icon: Wallet, label: 'Accounts' },
  { to: '/payments', icon: CreditCard, label: 'Payments' },
  { to: '/providers', icon: Building2, label: 'Providers' },
  { to: '/currencies', icon: Coins, label: 'Currencies' },
  { to: '/countries', icon: Globe, label: 'Countries' },
  { to: '/reconciliation', icon: ClipboardCheck, label: 'Reconciliation' },
  { to: '/fees', icon: Wrench, label: 'Fees & Commissions' },
  { to: '/fx', icon: ArrowLeftRight, label: 'FX Transactions' },
  { to: '/compliance', icon: Shield, label: 'Compliance' },
  { to: '/audit-log', icon: Search, label: 'Audit Log' },
  { to: '/reports', icon: FileText, label: 'Reports' },
  { to: '/support-tickets', icon: LifeBuoy, label: 'Support Tickets' },
  { to: '/maintenance-fees', icon: Wrench, label: 'Maintenance Fees' },
  { to: '/system', icon: Database, label: 'System' },
];

function Sidebar() {
  const navigate = useNavigate();
  const { adminUser, logout } = useAdminAuthStore();
  const { selectedClient, clearSelectedClient } = useSelectedClientStore();
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  return (
    <aside className="flex h-screen w-64 flex-col border-r border-gray-200 bg-white">
      <div className="flex h-16 items-center border-b border-gray-200 px-4">
        <img
          src="/logo.png"
          alt="XChangeLab"
          className="h-8 w-auto max-w-full object-contain object-left"
        />
      </div>

      {selectedClient && (
        <div className="mx-3 mt-3 mb-1 px-3 py-2.5 bg-indigo-50 border border-indigo-100 rounded-lg">
          <div className="flex items-start justify-between gap-1">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-indigo-700 truncate">{getClientDisplayName(selectedClient) || selectedClient.fullName}</p>
              {selectedClient.publicId && (
                <p className="text-xs font-mono text-indigo-500 mt-0.5">{formatPublicIdDisplay(selectedClient.publicId)}</p>
              )}
            </div>
            <button
              onClick={clearSelectedClient}
              title="Clear client context"
              className="flex-shrink-0 rounded p-0.5 text-indigo-300 hover:text-indigo-600 hover:bg-indigo-100 transition-colors mt-0.5"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-3">
        {navItems.map(({ to, icon: Icon, label }) => {
          const isScoped = CLIENT_SCOPED_ROUTES.has(to) && !!selectedClient;
          return (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={18} className={isActive ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-600'} />
                  <span className="flex-1">{label}</span>
                  {isScoped && !isActive && (
                    <span className="flex-shrink-0 w-2 h-2 rounded-full bg-indigo-400" title={`Filtered: ${selectedClient ? (getClientDisplayName(selectedClient) || selectedClient.fullName) : ''}`} />
                  )}
                  {isActive && <ChevronRight size={14} className="text-indigo-400" />}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-gray-200 p-3">
        <NavLink
          to="/settings"
          end
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
              isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`
          }
        >
          <Settings size={18} className="text-gray-400" />
          <span>Settings</span>
        </NavLink>
        <NavLink
          to="/settings/team"
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
              isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`
          }
        >
          <UserCog size={18} className="text-gray-400" />
          <span>Team</span>
        </NavLink>

        <div className="mt-1 flex items-center gap-3 rounded-lg px-3 py-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
            <Shield size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-gray-900">
              {adminUser ? `${adminUser.firstName || ''} ${adminUser.lastName || adminUser.email}`.trim() : 'Admin User'}
            </p>
            <p className="truncate text-xs text-gray-500">{adminUser?.email || 'admin@xchangelab.com'}</p>
          </div>
          <button
            onClick={handleLogout}
            title="Sign out"
            className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
